// Sketch document model + drawing. Everything is vector (strokes + text labels) on a fixed logical
// sheet, so the same drawing renders crisply at any screen size, exports to a picture, and can be
// saved as small JSON and opened again for editing.
import { decodeImage } from './media.js';

export const COLORS = ['#111111', '#d00207', '#294566', '#1a8f3c'];
export const WIDTHS = [5, 9, 16]; // line widths, in sheet units (the sheet is 1600 on its long edge)
export const LABEL_SIZES = [40, 56, 78];
export const SRC_MIME = 'application/vnd.onsite-sketch+json';

const FONT = (n) => `600 ${n}px Roboto, system-ui, Arial, sans-serif`;

export function newDoc(portrait) {
  return { v: 1, w: portrait ? 1200 : 1600, h: portrait ? 1600 : 1200, strokes: [], labels: [], hasBg: false };
}
export const isBlank = (d) => !d.strokes.length && !d.labels.length;

// Defensive: JSON coming back from NextCloud (or an old draft) may be missing bits.
export function cleanDoc(raw) {
  const w = Number(raw?.w) || 1600;
  const h = Number(raw?.h) || 1200;
  return {
    v: 1, w, h, hasBg: !!raw?.hasBg,
    strokes: (Array.isArray(raw?.strokes) ? raw.strokes : []).filter((s) => Array.isArray(s?.pts) && s.pts.length),
    labels: (Array.isArray(raw?.labels) ? raw.labels : []).filter((l) => typeof l?.text === 'string'),
  };
}

function trace(ctx, pts) {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  if (pts.length === 1) { ctx.lineTo(pts[0][0] + 0.01, pts[0][1]); return; } // a dot (round cap)
  for (let i = 1; i < pts.length - 1; i++) {
    ctx.quadraticCurveTo(pts[i][0], pts[i][1], (pts[i][0] + pts[i + 1][0]) / 2, (pts[i][1] + pts[i + 1][1]) / 2);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last[0], last[1]);
}

export function drawDoc(ctx, doc, bg, current) {
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, doc.w, doc.h);
  if (bg) { // background photo: scaled to fit (never stretched), centred
    const s = Math.min(doc.w / bg.width, doc.h / bg.height);
    const dw = bg.width * s;
    const dh = bg.height * s;
    ctx.drawImage(bg, (doc.w - dw) / 2, (doc.h - dh) / 2, dw, dh);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of current ? [...doc.strokes, current] : doc.strokes) {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width;
    trace(ctx, s.pts);
    ctx.stroke();
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const l of doc.labels) {
    ctx.font = FONT(l.size);
    ctx.lineWidth = Math.max(6, l.size / 4); // white halo keeps text readable over a photo
    ctx.strokeStyle = '#fff';
    ctx.strokeText(l.text, l.x, l.y);
    ctx.fillStyle = l.color;
    ctx.fillText(l.text, l.x, l.y);
  }
}

// ---- hit testing (eraser, label move/edit) -------------------------------------------------------
let mctx;
function textWidth(text, size) {
  mctx ||= document.createElement('canvas').getContext('2d');
  mctx.font = FONT(size);
  return mctx.measureText(text).width;
}
export function labelAt(doc, [x, y]) {
  for (let i = doc.labels.length - 1; i >= 0; i--) {
    const l = doc.labels[i];
    const w = textWidth(l.text, l.size) + 28;
    const h = l.size + 24;
    if (Math.abs(x - l.x) <= w / 2 && Math.abs(y - l.y) <= h / 2) return l;
  }
  return null;
}

function distToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const t = dx || dy ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy))) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
export function strokeHit(s, [x, y], r) {
  const reach = r + s.width / 2;
  if (s.pts.length === 1) return Math.hypot(x - s.pts[0][0], y - s.pts[0][1]) <= reach;
  for (let i = 1; i < s.pts.length; i++) {
    if (distToSegment(x, y, s.pts[i - 1][0], s.pts[i - 1][1], s.pts[i][0], s.pts[i][1]) <= reach) return true;
  }
  return false;
}

// ---- export / background --------------------------------------------------------------------------
// PNG for pure line drawings; JPEG when a photo is underneath (much smaller).
export async function exportImage(doc, bg) {
  const c = document.createElement('canvas');
  c.width = doc.w;
  c.height = doc.h;
  drawDoc(c.getContext('2d'), doc, bg, null);
  const mime = bg ? 'image/jpeg' : 'image/png';
  const blob = await new Promise((res) => c.toBlob(res, mime, 0.88));
  if (!blob) throw new Error('export_failed');
  return { blob, mime };
}

// A background photo shrunk to ~1600px on its long edge (keeps the saved source small).
export async function prepareBackground(file) {
  const bmp = await decodeImage(file);
  const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) throw new Error('bg_failed');
  return { blob, bitmap: await decodeImage(blob, false) };
}

export const randomHex = (n) => [...crypto.getRandomValues(new Uint8Array(n / 2))].map((b) => b.toString(16).padStart(2, '0')).join('');
